import axios from '@/plugins/axiosInstance'
import { entity } from '@/networking/urlManager'
import { cacheService } from '@/utils/cache'
import { CACHE_KEYS } from '../../constants'

// Aktif istekleri izleyen bir Map nesnesi
const activeRequests = new Map()

/**
 * İçerik listesini API'den çeker ve önbellekler
 * @param {string} assetId - Asset ID
 * @param {Object} params - İstek parametreleri
 * @returns {Promise<Object>} İçerik listesi sonucu
 */
export const fetchContentsList = async function (assetId, params = {}) {
  try {
    // Sarmalama değişkenleri
    const fetchStartTime = Date.now()
    const forceRefresh = params.forceRefresh === true

    // Önbellek durum bilgisini logla
    if (process.env.VUE_APP_MODE === 'development' && forceRefresh) {
      console.log(`Cache: v${this.cacheVersion}, ForceRefresh: ${forceRefresh}`)
    }

    // Normalize edilmiş parametreler
    const queryParams = this._normalizeParams({ ...params, assetId })

    // Bu istek için benzersiz bir anahtar oluştur
    // Cache anahtarı için versiyon numarasını ekle
    const cacheKey = JSON.stringify({ ...queryParams, _v: this.cacheVersion })

    // Eğer aynı istek zaten yapılıyorsa, o isteğin sonucunu bekle
    if (activeRequests.has(cacheKey)) {
      if (process.env.VUE_APP_MODE === 'development') {
        console.log('Aynı istek zaten devam ediyor, mevcut isteği kullanma')
      }
      return activeRequests.get(cacheKey)
    }

    // Önbellek anahtarı oluştur
    const cacheParams = { ...queryParams, _v: this.cacheVersion }
    delete cacheParams.forceRefresh
    // selectedFilter parametresi silinmiyor, cache anahtarı için gerekli
    // delete cacheParams.selectedFilter;

    // Önbellekte veri kontrolü - forceRefresh false ise
    let cachedData = null

    if (!forceRefresh) {
      cachedData = cacheService.get(CACHE_KEYS.CONTENT_LIST, cacheParams)

      if (process.env.VUE_APP_MODE === 'development' && cachedData) {
        console.log(`👏 Cache HIT`)
      }

      if (cachedData) {
        // Önbellekteki veriyi kullan
        this.contentsList = cachedData.detail
        this.totalCount = cachedData.totalCount
        this.pageCount = cachedData.pageCount
        this.lastActiveView = 'list'
        return cachedData
      }
    } else if (process.env.VUE_APP_MODE === 'development') {
      console.log('ForceRefresh kullanılıyor, önbellek bypass ediliyor')
    }

    // İstek sayacını artır
    this.requestCounter++

    // API isteği için parametreleri hazırla
    const apiParams = { ...queryParams }

    if (apiParams.selectedFilter !== undefined) {
      apiParams.filter = apiParams.selectedFilter
      delete apiParams.selectedFilter
    }

    if (process.env.VUE_APP_MODE === 'development') {
      console.log('API isteği gönderiliyor:', apiParams)
    }

    // İsteği aktif istekler listesine ekle
    const requestPromise = axios.get(entity.getAll, {
      params: apiParams,
    })

    // Temizleme fonksiyonu ile birlikte promise'i sakla
    activeRequests.set(cacheKey, requestPromise.then(response => {
      // İstek tamamlandığında aktif isteklerden çıkar
      activeRequests.delete(cacheKey)
      return response
    }).catch(err => {
      // Hata durumunda da aktif isteklerden çıkar
      activeRequests.delete(cacheKey)
      throw err
    }))

    // API isteği
    const response = await activeRequests.get(cacheKey)

    const data = response.data

    // Sonuçları store state'ine kaydet
    this.contentsList = data.data.detail
    this.totalCount = data.data.totalCount
    this.pageCount = data.data.pageCount

    // Veriyi önbellekle
    const result = {
      detail: data.data.detail,
      totalCount: data.data.totalCount,
      pageCount: data.data.pageCount,
    }

    cacheService.set(CACHE_KEYS.CONTENT_LIST, cacheParams, result, {
      ttl: this._getListTTL(result),
    })

    // Görünüm bilgisini güncelle
    this.lastActiveView = 'list'
    this.lastRefreshTime = Date.now()

    if (process.env.VUE_APP_MODE === 'development') {
      const logTime = Date.now() - fetchStartTime
      console.log(`👏 API: ${result.detail.length} items (${logTime}ms)`)
    }

    return result
  } catch (error) {
    console.error('Error fetching contents list:', error)
    return {
      detail: [],
      totalCount: 0,
      pageCount: 0,
    }
  }

}

/**
 * İçerik detayını API'den çeker ve önbellekler
 * @param {string} contentId - İçerik ID'si
 * @param {string} assetId - Asset ID'si
 * @returns {Promise<Object|null>} İçerik detayı
 */
export const fetchContentDetail = async function (contentId, assetId) {
  try {
    // Bu istek için benzersiz bir anahtar oluştur (deduplication için)
    const cacheKey = `detail-${contentId}-${assetId}`

    // Eğer aynı istek zaten yapılıyorsa, o isteğin sonucunu bekle
    if (activeRequests.has(cacheKey)) {
      if (process.env.VUE_APP_MODE === 'development') {
        console.log('Aynı detail isteği zaten devam ediyor, mevcut isteği kullanma')
      }
      return activeRequests.get(cacheKey)
    }

    // Önbellekte veri kontrolü - Cache anahtarını oluştur
    const cacheParams = { contentId, _v: this.cacheVersion }
    const cachedData = cacheService.get(CACHE_KEYS.CONTENT_DETAIL, cacheParams)

    // Önbellekte veri varsa kullan
    if (cachedData) {
      // Görünüm bilgisini güncelle
      this.lastActiveView = 'detail'

      if (process.env.VUE_APP_MODE === 'development') {
        console.log(`Önbellekten içerik detayı yüklendi (ID: ${contentId})`)
      }

      return cachedData
    }

    // İsteği aktif istekler listesine ekle
    const requestPromise = axios.get(entity.getDetail, {
      params: {
        entityId: contentId,
        assetId,
      },
    })

    // Temizleme fonksiyonu ile birlikte promise'i sakla
    activeRequests.set(cacheKey, requestPromise.then(response => {
      // İstek tamamlandığında aktif isteklerden çıkar
      activeRequests.delete(cacheKey)
      return response
    }).catch(err => {
      // Hata durumunda da aktif isteklerden çıkar
      activeRequests.delete(cacheKey)
      throw err
    }))

    // API isteği
    const response = await activeRequests.get(cacheKey)

    if (response?.data?.data) {
      const contentData = response.data.data

      // Önbellekte sakla - cache versiyonunu da ekleyerek
      cacheService.set(CACHE_KEYS.CONTENT_DETAIL, cacheParams, contentData, {
        ttl: this._getDetailTTL(contentData),
      })

      // Görünüm bilgisini güncelle
      this.lastActiveView = 'detail'

      if (process.env.VUE_APP_MODE === 'development') {
        console.log(`API'den içerik detayı yüklendi ve önbelleklendi (ID: ${contentId})`)
      }

      return contentData
    }

    return null
  } catch (error) {
    console.error('Error fetching content detail:', error)
    return null
  }
}

/**
 * İçerik detayını önbelleğe kaydeder
 * @param {string} contentId - İçerik ID'si
 * @param {Object} contentData - İçerik verisi
 */
export const setContentDetail = function (contentId, contentData) {
  // Cache anahtarı oluştur (versiyon dahil)
  const cacheParams = { contentId, _v: this.cacheVersion }

  // Önbellekte sakla
  cacheService.set(CACHE_KEYS.CONTENT_DETAIL, cacheParams, contentData, {
    ttl: this._getDetailTTL(contentData),
  })

  // Görünüm bilgisini güncelle
  this.lastActiveView = 'detail'

  if (process.env.VUE_APP_MODE === 'development') {
    console.log(`İçerik detayı önbelleğe kaydedildi (ID: ${contentId})`)
  }
}

/**
 * Önbellekten içerik detayını alır
 * @param {string} contentId - İçerik ID'si
 * @returns {Object|null} İçerik detayı
 */
export const getContentDetail = function (contentId) {
  // Cache anahtarı (versiyon dahil)
  const cacheParams = { contentId, _v: this.cacheVersion }
  return cacheService.get(CACHE_KEYS.CONTENT_DETAIL, cacheParams)
}

/**
 * İçerik ID'sini ayarlar
 * @param {string} contentId - İçerik ID'si
 */
export const setSelectedContentId = function (contentId) {
  this.selectedContentId = contentId
}

/**
 * Seçili içeriği temizler
 */
export const clearSelectedContent = function () {
  this.selectedContentId = null
}

/**
 * Arama parametrelerini ayarlar ve gerekirse önbelleği geçersiz kılar
 * @param {Object} params - Arama parametreleri
 */
export const setSearchParams = function (params) {
  let paramChanged = false

  // Her bir parametre için değişiklik kontrolü
  if (params.page !== undefined && this.currentPage !== params.page) {
    this.currentPage = params.page
    paramChanged = true
  }

  if (params.sortKey !== undefined && this.sortKey !== params.sortKey) {
    this.sortKey = params.sortKey
    paramChanged = true
  }

  if (params.sortOrder !== undefined && this.sortOrder !== params.sortOrder) {
    this.sortOrder = params.sortOrder
    paramChanged = true
  }

  if (params.searchQuery !== undefined && this.searchQuery !== params.searchQuery) {
    this.searchQuery = params.searchQuery
    paramChanged = true
  }

  if (params.selectedFilter !== undefined && this.selectedFilter !== params.selectedFilter) {
    this.selectedFilter = params.selectedFilter
    paramChanged = true
  }

  // Parametre değişiminde önbelleği geçersiz kıl
  if (paramChanged) {
    // İlgili önbellekleri temizle
    this._invalidateContentListCache()
  }
}

/**
 * Sayfa bilgilerini ayarlar
 * @param {Object} pageInfo - Sayfa bilgileri
 */
export const setPageInfo = function (pageInfo) {
  this.currentPage = pageInfo.currentPage
  this.totalCount = pageInfo.totalCount
  this.pageCount = pageInfo.pageCount
}

/**
 * İçerik store'unu tamamen sıfırlar
 * İçerik kaydedildikten veya iptal edildikten sonra kullanılır
 */
export const resetContentStore = function() {
  // Tüm store state'ini başlangıç durumuna getir
  this.contentsList = []
  this.selectedContentId = null
  this.totalCount = 0
  this.pageCount = 0
  this.currentPage = 1
  this.searchQuery = ''
  this.sortKey = 'createdAt'
  this.sortOrder = 'desc'
  this.selectedFilter = ''
  
  // Cache versiyon numarasını artır (tüm önbellekleri geçersiz kılar)
  this._incrementCacheVersion()
  
  // Görünüm bilgisini güncelle
  this.lastActiveView = 'list'
  
  if (process.env.VUE_APP_MODE === 'development') {
    console.log('Content store tamamen sıfırlandı')
  }
}